/* Rhody Reviews api/v1 -- reviews domain. PURE EXTRACTION from lib/api.ts (gate-prep split); no behavior change. */
import { request } from './_http';

export interface Review {
  /* The row's stable DB uuid. Distinct from review_id (the platform's own
     review id). The manual-reply endpoint keys on this uuid. */
  id?: string;
  review_id: string;
  platform: 'google' | 'yelp' | 'facebook';
  reviewer_name: string;
  rating: number;
  review_text: string;
  review_date: string;
  reply_text: string | null;
  reply_timestamp: string | null;
  /* Pending AI-draft reply folded in from the outbox (status
     pending_approval). draft_id is the outbox row the approve/edit/skip
     endpoints act on. Both null when the review has no pending draft. */
  draft_id: string | null;
  draft_response: string | null;
  /* True when this review's pending draft has been flagged for owner
     attention (outbox.escalated). False when there is no draft or it is not
     flagged. Drives the "Escalated" badge in the inbox. */
  escalated?: boolean;
}

export interface ReviewsResponse {
  client_id: string;
  count: number;
  reviews: Review[];
}

/* A pending AI-draft reply + its source review (GET /api/v1/outbox). */
export interface OutboxDraft {
  draft_id: string;
  review_id_ref: string | null;
  draft_response: string | null;
  status: string;
  drafted_at: string | null;
  review: {
    review_id: string;
    platform: Review['platform'];
    reviewer_name: string;
    rating: number;
    review_text: string;
    review_date: string;
  } | null;
}

export interface OutboxResponse {
  client_id: string;
  count: number;
  drafts: OutboxDraft[];
}

/* Result of an approve / edit / skip action on an outbox draft. */
export interface OutboxActionResult {
  client_id: string;
  draft_id: string;
  status: string;
  posted_at?: string | null;
  final_response?: string | null;
}

/* Result of regenerating a draft (POST /outbox/<id>/regenerate). The new AI
   draft text comes back; status stays pending_approval. */
export interface RegenerateResult {
  client_id: string;
  draft_id: string;
  status: string;
  draft_response: string;
}

/* Result of recording a private message (POST /outbox/<id>/private-message).
   transmitted is always false -- dry-run, nothing is sent. */
export interface PrivateMessageResult {
  client_id: string;
  draft_id: string;
  status: string;
  private_note: string;
  transmitted: boolean;
}

/* Result of escalating a draft (POST /outbox/<id>/escalate). */
export interface EscalateResult {
  client_id: string;
  draft_id: string;
  status: string;
  escalated: boolean;
  escalated_at: string | null;
  escalation_note?: string | null;
}

/* Result of a manual owner reply (POST /reviews/<id>/reply). status is
   "posted"; final_response echoes the saved text. */
export interface WriteReplyResult {
  client_id?: string;
  review_id?: string;
  status: string;
  final_response: string;
  posted_at?: string | null;
}

export const reviewsApi = {
  listReviews(
    params: { since?: string; platform?: Review['platform']; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<ReviewsResponse> {
    return request<ReviewsResponse>('/api/v1/reviews', { query: params, signal });
  },

  /* Pending AI-draft replies awaiting approval (the approval queue). */
  listOutbox(signal?: AbortSignal): Promise<OutboxResponse> {
    return request<OutboxResponse>('/api/v1/outbox', { signal });
  },

  /* Write and post a MANUAL reply to a review that has no AI draft. Fires ONLY
     on the owner's explicit submit (never auto-posts). text is 2-4000 chars.
     Errors (via ApiError.status + .message): 400 missing_text / text_too_long,
     404 review_not_found, 409 already_replied, 409 draft_exists (a pending AI
     draft exists -- the response detail carries draft_id so the UI can route to
     approve/edit instead). */
  writeReply(reviewId: string, text: string): Promise<WriteReplyResult> {
    return request<WriteReplyResult>(
      `/api/v1/reviews/${encodeURIComponent(reviewId)}/reply`,
      { method: 'POST', body: { text } },
    );
  },

  /* Edit a reply that ALREADY exists on a review (posted to Google, or recorded
     here). Same endpoint as writeReply with edit:true, so it skips the
     already_replied guard and routes through the approved-reply path. For a
     v4-native Google review this PUT-replaces the existing owner reply; for a
     Places-excerpt review it updates the recorded reply. Fires ONLY on the
     owner's explicit "Update reply" click (never auto-posts). */
  editReply(reviewId: string, text: string): Promise<WriteReplyResult> {
    return request<WriteReplyResult>(
      `/api/v1/reviews/${encodeURIComponent(reviewId)}/reply`,
      { method: 'POST', body: { text, edit: true } },
    );
  },

  /* Approve a draft as-drafted and record it posted. Fires ONLY on an
     explicit operator click. Replies never auto-post (Hard Rule #1). */
  approveDraft(draftId: string): Promise<OutboxActionResult> {
    return request<OutboxActionResult>(
      `/api/v1/outbox/${encodeURIComponent(draftId)}/approve`,
      { method: 'POST', body: {} },
    );
  },

  /* Save an edited reply body, approve it, and record it posted. */
  editDraft(draftId: string, finalResponse: string): Promise<OutboxActionResult> {
    return request<OutboxActionResult>(
      `/api/v1/outbox/${encodeURIComponent(draftId)}/edit`,
      { method: 'POST', body: { final_response: finalResponse } },
    );
  },

  /* Skip/reject a draft. It is never posted; the review stays open. */
  skipDraft(draftId: string, notes?: string): Promise<OutboxActionResult> {
    return request<OutboxActionResult>(
      `/api/v1/outbox/${encodeURIComponent(draftId)}/skip`,
      { method: 'POST', body: notes ? { notes } : {} },
    );
  },

  /* Re-run the AI draft for this review and replace the draft text in place.
     Status stays pending_approval -- regeneration never approves or posts. */
  regenerateDraft(draftId: string): Promise<RegenerateResult> {
    return request<RegenerateResult>(
      `/api/v1/outbox/${encodeURIComponent(draftId)}/regenerate`,
      { method: 'POST', body: {} },
    );
  },

  /* Record a PRIVATE response to the reviewer (instead of a public reply).
     DRY-RUN: stored only, never transmitted. */
  privateMessageDraft(draftId: string, message: string): Promise<PrivateMessageResult> {
    return request<PrivateMessageResult>(
      `/api/v1/outbox/${encodeURIComponent(draftId)}/private-message`,
      { method: 'POST', body: { message } },
    );
  },

  /* Flag a drafted reply for owner attention (persisted status flag). Does
     not post, approve, or skip. */
  escalateDraft(draftId: string, note?: string): Promise<EscalateResult> {
    return request<EscalateResult>(
      `/api/v1/outbox/${encodeURIComponent(draftId)}/escalate`,
      { method: 'POST', body: note ? { note } : {} },
    );
  },
};
